import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState, useCallback } from "react";
import { Accordion, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { AccordionButton } from "../../components/Common/Accordion/AccordionButton";
import { PageTitle } from "../../components/Common/PageTitle";
import { ModalConfirm } from "../../components/ModalConfirm";
import { useFaq } from "../../hooks/faq";
import { useMe } from "../../hooks/queries";
import { CreateFaqRequest, FaqData } from "../../models/FaqData";
import DataSvc from "../../services/dataSvc";

import "./styles.scss";

export const FaqPage: React.FunctionComponent = () => {
  const { t: _t } = useTranslation();
  const t = useCallback((key: string) => _t(`faqPage.${key}`), [_t]);
  const queryClient = useQueryClient();

  const { data: userData } = useMe();
  const isAdmin = userData?.type === "admin";

  const [question, setQuestion] = useState<string>("");
  const [answer, setAnswer] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [faqToDelete, setFaqToDelete] = useState<number | null>(null);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  // Use React Query to fetch FAQs
  const { data: faqs, isLoading: loading } = useFaq();

  // Initialize all FAQs as open when data is loaded
  React.useEffect(() => {
    if (faqs && faqs.length > 0) {
      setActiveKeys(faqs.map((faq: FaqData) => faq.id.toString()));
    }
  }, [faqs]);

  const handleAccordionSelect = (eventKey: AccordionEventKey) => {
    if (eventKey !== null && eventKey !== undefined) {
      setActiveKeys((prevKeys) => {
        const key = eventKey?.toString();
        const isCurrentlyActive = prevKeys.includes(key);
        if (isCurrentlyActive) {
          return prevKeys.filter((k) => k !== key);
        } else {
          return [...prevKeys, key];
        }
      });
    }
  };

  // Create FAQ mutation
  const createFaqMutation = useMutation({
    mutationFn: (request: CreateFaqRequest) => DataSvc.createFaq(request),
    onSuccess: () => {
      toast.success(t("faq_created_successfully"));
      setQuestion("");
      setAnswer("");
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
    },
    onError: (error) => {
      console.error("Error creating FAQ:", error);
      toast.error(t("error_creating_faq"));
    }
  });

  // Delete FAQ mutation
  const deleteFaqMutation = useMutation({
    mutationFn: (faqId: number) => DataSvc.deleteFaq(faqId),
    onSuccess: () => {
      toast.success(t("faq_deleted_successfully"));
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
    },
    onError: (error) => {
      console.error("Error deleting FAQ:", error);
      toast.error(t("error_deleting_faq"));
    }
  });

  const handleCreateFaq = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim() || !answer.trim()) {
      toast.error(t("question_and_answer_required"));
      return;
    }

    const createRequest: CreateFaqRequest = {
      question: question.trim(),
      answer: answer.trim()
    };

    createFaqMutation.mutate(createRequest);
  };

  const handleDeleteFaq = (faqId: number) => {
    setFaqToDelete(faqId);
    setShowDeleteModal(true);
  };

  const confirmDeleteFaq = () => {
    if (faqToDelete) {
      deleteFaqMutation.mutate(faqToDelete);
      setShowDeleteModal(false);
      setFaqToDelete(null);
    }
  };

  const cancelDeleteFaq = () => {
    setShowDeleteModal(false);
    setFaqToDelete(null);
  };

  return (
    <div className="right-content faq-page">
      <div className="top-title-bar flex-grid">
        <PageTitle text={t("title")} />
        <div className="line"></div>
      </div>

      {/* Admin section for creating FAQs */}
      {isAdmin && (
        <div className="detail-group create-faq-group">
          <div className="group-title">
            <div className="line"></div>
          </div>
          <div className="white-border-panel create-faq-panel">
            <Form onSubmit={handleCreateFaq}>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("question_label")}</Form.Label>
                    <br />
                    <Form.Text>{t("admin_warning_text")}</Form.Text>
                    <Form.Control
                      type="text"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder={t("question_placeholder")}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder={t("answer_placeholder")}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Button
                    className="btn btn-blue"
                    type="submit"
                    variant="primary"
                    disabled={createFaqMutation.isLoading}
                  >
                    {createFaqMutation.isLoading
                      ? t("creating")
                      : t("create_faq")}
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      )}

      <div className="white-border-panel faq-panel">
        <div className="faq-list">
          {loading ? (
            <div className="text-center">{t("loading")}</div>
          ) : (faqs?.length ?? 0) === 0 ? (
            <Alert variant="info">{t("no_faqs_available")}</Alert>
          ) : (
            <Accordion activeKey={activeKeys} onSelect={handleAccordionSelect}>
              {(faqs ?? []).map((faq: FaqData) => (
                <Accordion.Item
                  key={faq.id.toString()}
                  eventKey={faq.id.toString()}
                >
                  <AccordionButton accordionItemKey={faq.id.toString()}>
                    <div className="w-100 faq-question-container">
                      <span className="faq-question-text">{faq.question}</span>
                    </div>
                  </AccordionButton>
                  <Accordion.Body>
                    <div className="faq-answer">
                      {faq.answer
                        .split(/\\n|\n/)
                        .map((line: string, lineIndex: number) => (
                          <p key={lineIndex}>{line}</p>
                        ))}
                    </div>
                    {isAdmin && (
                      <div className="mt-3 d-flex justify-content-end">
                        <Button
                          className="btn btn-red"
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteFaq(faq.id)}
                          disabled={deleteFaqMutation.isLoading}
                        >
                          {t("delete")}
                        </Button>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <ModalConfirm
          title={"confirm_delete_faq"}
          cancelLabel={"cancel"}
          confirmLabel={"confirm"}
          onClose={cancelDeleteFaq}
          onConfirm={confirmDeleteFaq}
          color="red"
          translate
        />
      )}
    </div>
  );
};
